import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "./AuthContext";
import useGraphql from "../Hooks/useGraphql";
import { GET_CONSOLE_HELP_LINKS } from "../graphql/consoleHelpGraphql";

const Context = createContext(null);

export function ConsoleHelpLinksProvider({ children }) {
  const auth = useAuthContext();
  const { pathname, search } = useLocation();
  const { axiosGraphQL } = useGraphql();
  const uid = auth.firebaseUser?.uid;
  const userId = auth.authState?.user?._id;
  const impersonateId = auth.authState?.impersonate?._id || "";
  const ready = Boolean(auth.isAuthenticated?.() && uid && userId);
  const scope = ready ? JSON.stringify([uid, userId, impersonateId]) : null;
  const current = useRef({ scope, uid, axiosGraphQL });
  current.current = { scope, uid, axiosGraphQL };
  const mounted = useRef(true);
  const request = useRef(0);
  const [state, setState] = useState({ scope: null, configuration: null, loading: false, error: null });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; };
  }, []);

  const refresh = useCallback(async () => {
    const snapshot = current.current;
    if (!snapshot.scope) return null;
    const version = ++request.current;
    const isCurrent = () => mounted.current && current.current.scope === snapshot.scope && request.current === version;
    setState(previous => ({
      scope: snapshot.scope, configuration: previous.scope === snapshot.scope ? previous.configuration : null,
      loading: true, error: null,
    }));
    try {
      const response = await snapshot.axiosGraphQL({ query: GET_CONSOLE_HELP_LINKS }, {
        withoutClub: true, expectedFirebaseUid: snapshot.uid,
      });
      const configuration = response?.data?.data?.getConsoleHelpLinks;
      if (!Number.isSafeInteger(configuration?.revision) || configuration.revision < 0 || !Array.isArray(configuration.links)) {
        throw new Error("invalid_help_configuration");
      }
      if (!isCurrent()) return null;
      setState({ scope: snapshot.scope, configuration, loading: false, error: null });
      return configuration;
    } catch (error) {
      if (!isCurrent()) return null;
      setState({ scope: snapshot.scope, configuration: null, loading: false, error });
      throw error;
    }
  }, []);

  // One request for all page headers; each route visit gets the latest mapping.
  useEffect(() => {
    request.current += 1;
    if (scope) refresh().catch(() => {});
    else setState({ scope: null, configuration: null, loading: false, error: null });
  }, [scope, pathname, search, refresh]);

  useEffect(() => {
    const onFocus = () => { refresh().catch(() => {}); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // A different account must never see the previous account's in-flight data.
  const visibleState = state.scope === scope ? state : { configuration: null, loading: ready, error: null };
  return <Context.Provider value={{ ...visibleState, scope, refresh }}>{children}</Context.Provider>;
}

export const useConsoleHelpLinks = () => useContext(Context);
